// src/pages/Users/UserDetailsPage.js
import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUserDetails,
  clearUserDetails,
  fetchUserClips,
  fetchUserAuctions,
  fetchUserOrders,
  fetchUserChannels,
  updateUserAuctionStatus,
  updateUserClipStatus,
  syncWithMyFatorah,
  checkMyFatorahStatus,
} from '../../redux/slices/userSlice';
import { useTranslation } from '../../contexts/LanguageContext';
import VideoModal from '../../components/Users/VideoModal';
import ImageModal from '../../components/Users/ImageModal';
import VerificationStatusModal from '../../components/Users/VerificationStatusModal';
import MyFatorahStatusModal from '../../components/Users/MyFatorahStatusModal';
import OrderDetailsModal from '../../components/Orders/OrderDetailsModal';
import Avatar from '../../components/Common/Avatar';
import Pagination from '../../components/Common/Pagination';

const UserDetailsPage = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, tStatus } = useTranslation();
  
  // Get user data passed via navigation state
  const passedUserData = location.state?.userData;
  
  const {
    userDetails,
    clips, clipsTotal, clipsTotalPages, clipsCurrentPage, loadingClips, errorClips,
    auctions, auctionsTotal, auctionsTotalPages, auctionsCurrentPage, loadingAuctions, errorAuctions,
    userOrders, ordersTotal, ordersTotalPages, ordersCurrentPage, loadingOrders, errorOrders,
    userChannels, channelsTotal, channelsTotalPages, channelsCurrentPage, loadingChannels, errorChannels,
    loading, error, syncingMyFatorah, errorMyFatorah
  } = useSelector((state) => state.users);

  // Get auth token at component level
  const { token } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState('profile');
  const [videoModalData, setVideoModalData] = useState({ open: false, videoUrl: '' });
  const [imageModalData, setImageModalData] = useState({ open: false, imageUrl: '', altText: '' });
  const [failedImages, setFailedImages] = useState(new Set()); // Track failed image URLs
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [myFatorahStatusModalOpen, setMyFatorahStatusModalOpen] = useState(false);
  const [myFatorahStatusData, setMyFatorahStatusData] = useState(null);
  const [orderDetailsToView, setOrderDetailsToView] = useState(null);

  // Parse URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const tabParam = urlParams.get('tab');
  const auctionIdParam = urlParams.get('auctionId');
  const clipIdParam = urlParams.get('clipId');
  const orderIdParam = urlParams.get('orderId');
  const channelIdParam = urlParams.get('channelId');

  // Pagination settings
  const ITEMS_PER_PAGE = 10;

  // show up to `numWords` words, then "…" if longer
  const truncateWords = (text = "", numWords = 15) => {
    const words = text.split(/\s+/);
    if (words.length <= numWords) return text;
    return words.slice(0, numWords).join(" ") + "…";
  };

  // Handle keyboard events for modal closing
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (imageModalData.open) {
          setImageModalData({ open: false, imageUrl: '', altText: '' });
        }
        if (videoModalData.open) {
          setVideoModalData({ open: false, videoUrl: '' });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [imageModalData.open, videoModalData.open]);

  // Handle URL parameters on component mount
  useEffect(() => {
    if (tabParam && tabParam === 'auction') {
      setActiveTab('auction');
    }
    if (tabParam && tabParam === 'clips') {
      setActiveTab('clips');
    }
    if (tabParam && tabParam === 'verification') {
      setActiveTab('verification');
    }
    if (tabParam && tabParam === 'orders') {
      setActiveTab('orders');
    }
    if (tabParam && tabParam === 'channels') {
      setActiveTab('channels');
    }
  }, [tabParam]);

  useEffect(() => {
    // If we have passed user data (from verification click), use it directly
    if (passedUserData) {
      // Manually set the user details without calling the API
      dispatch({ 
        type: 'users/setUserDetails', 
        payload: passedUserData 
      });
    } else {
      // Normal flow - fetch user details from API
      dispatch(fetchUserDetails(userId));
    }
    return () => dispatch(clearUserDetails());
  }, [dispatch, userId, passedUserData]);

  // Fetch data when tabs activate or page changes
  useEffect(() => {
    if (activeTab === "clips") {
      dispatch(fetchUserClips({ 
        userId, 
        currentPage: clipsCurrentPage, 
        limit: ITEMS_PER_PAGE 
      }));
    }
    if (activeTab === "auction") {
      dispatch(fetchUserAuctions({
        userId,
        currentPage: auctionsCurrentPage,
        limit: ITEMS_PER_PAGE
      }));
    }
    if (activeTab === "orders") {
      dispatch(fetchUserOrders({
        userId,
        currentPage: ordersCurrentPage,
        limit: ITEMS_PER_PAGE,
      }));
    }
    if (activeTab === "channels") {
      dispatch(fetchUserChannels({
        userId,
        currentPage: channelsCurrentPage,
        limit: ITEMS_PER_PAGE,
      }));
    }
  }, [dispatch, userId, activeTab, clipsCurrentPage, auctionsCurrentPage, ordersCurrentPage, channelsCurrentPage]);

  // When arriving with ?tab=orders&orderId=..., auto-open that order's detail modal
  // once the user's orders list has loaded and contains the requested order.
  useEffect(() => {
    if (activeTab !== "orders" || !orderIdParam) return;
    const match = userOrders.find(
      (o) => o._id === orderIdParam || o.customOrderId === orderIdParam
    );
    if (match) setOrderDetailsToView(match);
  }, [activeTab, orderIdParam, userOrders]);

  // Handle tab changes - reset to page 1 when switching tabs
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    // Reset pagination when switching tabs will be handled by useEffect
  };

  // Handle pagination for clips
  const handleClipsPageChange = (page) => {
    dispatch(fetchUserClips({ 
      userId, 
      currentPage: page, 
      limit: ITEMS_PER_PAGE 
    }));
  };

  // Handle pagination for auctions
  const handleAuctionsPageChange = (page) => {
    dispatch(fetchUserAuctions({
      userId,
      currentPage: page,
      limit: ITEMS_PER_PAGE
    }));
  };

  // Handle pagination for orders
  const handleOrdersPageChange = (page) => {
    dispatch(fetchUserOrders({
      userId,
      currentPage: page,
      limit: ITEMS_PER_PAGE,
    }));
  };

  // Handle pagination for channels
  const handleChannelsPageChange = (page) => {
    dispatch(fetchUserChannels({
      userId,
      currentPage: page,
      limit: ITEMS_PER_PAGE,
    }));
  };

  const renderTabs = () => {
    const tabs = [{ key: "profile", label: t("userDetails.tab.profile") }];

    // Add Verification Process tab if user has verification data
    if (userDetails?.identityVerification) {
      tabs.push({
        key: "verification",
        label: t("userDetails.tab.verification")
      });
    }

    // Always add Clips tab (regardless of features - let the content handle empty state)
    tabs.push({
      key: "clips",
      label: `${t("userDetails.tab.clips")} ${clipsTotal > 0 ? `(${clipsTotal})` : ''}`
    });

    // Always add Auctions tab (regardless of features - let the content handle empty state)
    tabs.push({
      key: "auction",
      label: `${t("userDetails.tab.auctions")} ${auctionsTotal > 0 ? `(${auctionsTotal})` : ''}`
    });

    // Orders tab — list of orders this user has placed.
    tabs.push({
      key: "orders",
      label: `${t("userDetails.tab.orders")} ${ordersTotal > 0 ? `(${ordersTotal})` : ''}`
    });

    // Channels tab — list of channels this user has created.
    tabs.push({
      key: "channels",
      label: `${t("userDetails.tab.channels")} ${channelsTotal > 0 ? `(${channelsTotal})` : ''}`
    });

    return tabs;
  };

  const openVideoModal = (url) => setVideoModalData({ open: true, videoUrl: url });

  const handleSeeAllClips = () => {
    // Clear clipIdParam and fetch all clips
    navigate(`/users/${userId}?tab=clips`);
    dispatch(fetchUserClips({ userId, currentPage: 1, limit: ITEMS_PER_PAGE }));
  };

  const handleSeeAllAuctions = () => {
    // Clear auctionIdParam and fetch all auctions
    navigate(`/users/${userId}?tab=auction`);
    dispatch(fetchUserAuctions({ userId, currentPage: 1, limit: ITEMS_PER_PAGE }));
  };

  const handleSeeAllOrders = () => {
    navigate(`/users/${userId}?tab=orders`);
    dispatch(fetchUserOrders({ userId, currentPage: 1, limit: ITEMS_PER_PAGE }));
  };

  const handleSeeAllChannels = () => {
    navigate(`/users/${userId}?tab=channels`);
    dispatch(fetchUserChannels({ userId, currentPage: 1, limit: ITEMS_PER_PAGE }));
  };

  // Safe image error handler to prevent infinite loops
  const handleImageError = (e, imageUrl) => {
    // Immediately remove the error handler to prevent infinite loops
    e.target.onerror = null;
    
    // Prevent infinite loops by tracking failed images
    if (!failedImages.has(imageUrl)) {
      setFailedImages(prev => new Set([...prev, imageUrl]));
      // Use a data URL as fallback instead of file path
      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f3f4f6" width="200" height="200"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage not available%3C/text%3E%3C/svg%3E';
    } else {
      // If even the placeholder fails, hide the image
      e.target.style.display = 'none';
    }
  };

  // Handle Sync with MyFatorah using Redux
  const handleSyncWithMyFatorah = async () => {
    console.log('handleSyncWithMyFatorah called');
    console.log('userDetails:', userDetails);
    
    if (!userDetails?._id) {
      alert('User ID not found');
      return;
    }

    // Temporary hardcoded approach to bypass any caching issues
    try {
      const hardcodedEndpoint = `http://43.204.150.34:3000/api/v2/admins/identity-verification/upload-supplier-documents-to-myfatoorah/${userDetails._id}`;
      
      console.log('Making direct API call to:', hardcodedEndpoint);
      
      const response = await fetch(hardcodedEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Success response:', data);
        alert('Successfully synced with MyFatorah!');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'API call failed');
      }
    } catch (error) {
      console.error('Error syncing with MyFatorah:', error);
      alert(`Failed to sync with MyFatorah: ${error.message || error || 'Unknown error'}`);
    }
  };

  // Handle Check MyFatorah Status
  const handleCheckMyFatorahStatus = async () => {
    console.log('handleCheckMyFatorahStatus called');
    console.log('userDetails:', userDetails);
    
    if (!userDetails?.identityVerification?.company?.supplierCode) {
      alert('Supplier Code not found');
      return;
    }

    const supplierCode = userDetails.identityVerification.company.supplierCode;
    console.log('Using supplierCode:', supplierCode);

    try {
      const hardcodedEndpoint = `http://43.204.150.34:3000/api/v2/admins/identity-verification/supplier-status/${supplierCode}`;
      
      console.log('Making direct API call to:', hardcodedEndpoint);
      
      const response = await fetch(hardcodedEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('MyFatorah Status response:', data);
        
        // Set the status data and open the modal
        setMyFatorahStatusData(data);
        setMyFatorahStatusModalOpen(true);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'API call failed');
      }
    } catch (error) {
      console.error('Error checking MyFatorah status:', error);
      alert(`Failed to check MyFatorah status: ${error.message || error || 'Unknown error'}`);
    }
  };

  const renderActiveTabContent = () => {
    if (activeTab === "profile") {
      const name = userDetails.name || "";
      // Resolve user type from identityVerification.type ('personal' | 'company').
      const verifType = userDetails?.identityVerification?.type;
      const userTypeLabel = verifType === 'personal'
        ? t("userDetails.personal")
        : verifType === 'company'
        ? t("userDetails.company")
        : t("userDetails.unknownType");
      // Resolve subscription display from userSubscription (planId, subscriptionType, status).
      const sub = userDetails?.userSubscription;
      let subscriptionLabel = t("userDetails.noSubscription");
      if (sub && (sub.subscriptionType || sub.planId)) {
        const cycle = sub.subscriptionType === 'monthly'
          ? t("plan.monthly")
          : sub.subscriptionType === 'yearly'
          ? t("plan.yearly")
          : sub.subscriptionType || "";
        const statusText = sub.status ? tStatus(sub.status) : "";
        subscriptionLabel = [cycle, statusText].filter(Boolean).join(" • ");
      }
      return (
        <div>
          <h2 className="text-xl font-bold mb-2">{t("userDetails.profileTitle")}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.fullName")}</label>
              <input readOnly value={name}
                     className="w-full border rounded p-2 text-sm"/>
            </div>
            {/* User Handle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.userHandle")}</label>
              <input readOnly value={userDetails.userHandle ? `@${userDetails.userHandle}` : "N/A"}
                     className="w-full border rounded p-2 text-sm"/>
            </div>
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.email")}</label>
              <input readOnly value={userDetails.email||"N/A"}
                     className="w-full border rounded p-2 text-sm"/>
            </div>
            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.phone")}</label>
              <input readOnly value={userDetails.phone||"N/A"}
                     className="w-full border rounded p-2 text-sm"/>
            </div>
            {/* User Type (Personal / Company) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.userType")}</label>
              <input readOnly value={userTypeLabel}
                     className="w-full border rounded p-2 text-sm"/>
            </div>
            {/* Account Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.accountStatus")}</label>
              <input readOnly value={tStatus(userDetails.accountStatus)}
                     className="w-full border rounded p-2 text-sm"/>
            </div>
            {/* Subscription Plan */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.subscriptionPlan")}</label>
              <input readOnly value={subscriptionLabel}
                     className="w-full border rounded p-2 text-sm"/>
            </div>
            {/* Country */}
            {userDetails.country && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.country")}</label>
                <input
                  readOnly
                  value={`${userDetails.country.countryName} (${userDetails.country.mobileCountrycode})`}
                  className="w-full border rounded p-2 text-sm"
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeTab === "verification") {
      const verification = userDetails?.identityVerification;
      if (!verification) {
        return (
          <div className="text-center py-8">
            <p className="text-gray-500">{t("userDetails.noVerification")}</p>
          </div>
        );
      }

      const verificationType = verification.type;
      console.log('Verification data:', verification); // Debug log
      console.log('Verification type:', verificationType); // Debug log

      return (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">{t("userDetails.verificationProcess")}</h2>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              verificationType === 'personal' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
            }`}>
              {verificationType === 'personal' ? t("userDetails.personal") : verificationType === 'company' ? t("userDetails.company") : verificationType} {t("userDetails.verificationSuffix")}
            </span>
          </div>

          {verificationType === 'personal' && verification.personal && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">{t("userDetails.personalInfo")}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.fullName")}</label>
                    <input readOnly value={verification.personal.fullName || "N/A"}
                           className="w-full border rounded p-2 text-sm bg-white"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.email")}</label>
                    <input readOnly value={verification.personal.email || "N/A"}
                           className="w-full border rounded p-2 text-sm bg-white"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.phoneNumber")}</label>
                    <input readOnly value={verification.personal.phoneNumber || "N/A"}
                           className="w-full border rounded p-2 text-sm bg-white"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.verificationType")}</label>
                    <input readOnly value={verification.personal.verificationType || "N/A"}
                           className="w-full border rounded p-2 text-sm bg-white"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.idNumber")}</label>
                    <input readOnly value={verification.personal.idNumber || "N/A"}
                           className="w-full border rounded p-2 text-sm bg-white"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.expiryDate")}</label>
                    <input readOnly value={verification.personal.expiryDate || "N/A"}
                           className="w-full border rounded p-2 text-sm bg-white"/>
                  </div>
                </div>
              </div>

              {/* Personal Address */}
              {verification.personal.address && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">{t("userDetails.addressInfo")}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.addressLine1")}</label>
                      <input readOnly value={verification.personal.address.addressLine1 || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.addressLine2")}</label>
                      <input readOnly value={verification.personal.address.addressLine2 || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.city")}</label>
                      <input readOnly value={verification.personal.address.city || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.state")}</label>
                      <input readOnly value={verification.personal.address.state || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.zipCode")}</label>
                      <input readOnly value={verification.personal.address.zipCode || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                  </div>
                </div>
              )}

              {/* Personal Document Images */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">{t("userDetails.documentImages")}</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {verification.personal.frontImageURL && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t("userDetails.field.frontImage")}</label>
                      <img 
                        src={verification.personal.frontImageURL} 
                        alt="Front ID" 
                        className="w-full h-48 object-cover rounded border shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setImageModalData({ 
                          open: true, 
                          imageUrl: verification.personal.frontImageURL, 
                          altText: 'Personal ID - Front Image' 
                        })}
                        onError={(e) => handleImageError(e, verification.personal.frontImageURL)}
                      />
                    </div>
                  )}
                  {verification.personal.backImageURL && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t("userDetails.field.backImage")}</label>
                      <img 
                        src={verification.personal.backImageURL} 
                        alt="Back ID" 
                        className="w-full h-48 object-cover rounded border shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setImageModalData({ 
                          open: true, 
                          imageUrl: verification.personal.backImageURL, 
                          altText: 'Personal ID - Back Image' 
                        })}
                        onError={(e) => handleImageError(e, verification.personal.backImageURL)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {verificationType === 'company' && verification.company && (
            <div className="space-y-6">
              {/* Company Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">{t("userDetails.companyInfo")}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.companyName")}</label>
                    <input readOnly value={verification.company.companyName || "N/A"}
                           className="w-full border rounded p-2 text-sm bg-white"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.companyEmail")}</label>
                    <input readOnly value={verification.company.companyEmail || "N/A"}
                           className="w-full border rounded p-2 text-sm bg-white"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.companyPhone")}</label>
                    <input readOnly value={verification.company.companyPhoneNumber || "N/A"}
                           className="w-full border rounded p-2 text-sm bg-white"/>
                  </div>
                </div>
              </div>

              {/* Company Address */}
              {verification.company.address && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">{t("userDetails.companyAddress")}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.addressLine1")}</label>
                      <input readOnly value={verification.company.address.addressLine1 || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.addressLine2")}</label>
                      <input readOnly value={verification.company.address.addressLine2 || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.city")}</label>
                      <input readOnly value={verification.company.address.city || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.state")}</label>
                      <input readOnly value={verification.company.address.state || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.zipCode")}</label>
                      <input readOnly value={verification.company.address.zipCode || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                  </div>
                </div>
              )}

              {/* Authorized Person Information */}
              {verification.company.authorizedPerson && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">{t("userDetails.authorizedPerson")}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.fullName")}</label>
                      <input readOnly value={verification.company.authorizedPerson.fullName || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.designation")}</label>
                      <input readOnly value={verification.company.authorizedPerson.designation || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.verificationType")}</label>
                      <input readOnly value={verification.company.authorizedPerson.verificationType || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("userDetails.field.idNumber")}</label>
                      <input readOnly value={verification.company.authorizedPerson.idNumber || "N/A"}
                             className="w-full border rounded p-2 text-sm bg-white"/>
                    </div>
                  </div>

                  {/* Authorized Person Document Images */}
                  <div className="mt-6">
                    <h4 className="text-md font-medium mb-3 text-gray-700">{t("userDetails.authorizedPersonDocs")}</h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      {verification.company.authorizedPerson.frontImageURL && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t("userDetails.field.frontImage")}</label>
                          <img 
                            src={verification.company.authorizedPerson.frontImageURL} 
                            alt="Authorized Person Front ID" 
                            className="w-full h-48 object-cover rounded border shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setImageModalData({ 
                              open: true, 
                              imageUrl: verification.company.authorizedPerson.frontImageURL, 
                              altText: 'Authorized Person ID - Front Image' 
                            })}
                            onError={(e) => handleImageError(e, verification.company.authorizedPerson.frontImageURL)}
                          />
                        </div>
                      )}
                      {verification.company.authorizedPerson.backImageURL && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t("userDetails.field.backImage")}</label>
                          <img 
                            src={verification.company.authorizedPerson.backImageURL} 
                            alt="Authorized Person Back ID" 
                            className="w-full h-48 object-cover rounded border shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setImageModalData({ 
                              open: true, 
                              imageUrl: verification.company.authorizedPerson.backImageURL, 
                              altText: 'Authorized Person ID - Back Image' 
                            })}
                            onError={(e) => handleImageError(e, verification.company.authorizedPerson.backImageURL)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Company Documents */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">{t("userDetails.companyDocuments")}</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {verification.company.companyLicenseURL && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t("userDetails.field.companyLicense")}</label>
                      <img 
                        src={verification.company.companyLicenseURL} 
                        alt="Company License" 
                        className="w-full h-48 object-cover rounded border shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setImageModalData({ 
                          open: true, 
                          imageUrl: verification.company.companyLicenseURL, 
                          altText: 'Company License Document' 
                        })}
                        onError={(e) => handleImageError(e, verification.company.companyLicenseURL)}
                      />
                    </div>
                  )}
                  {verification.company.signedCertificateURL && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t("userDetails.field.signedCertificate")}</label>
                      <img 
                        src={verification.company.signedCertificateURL} 
                        alt="Signed Certificate" 
                        className="w-full h-48 object-cover rounded border shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setImageModalData({ 
                          open: true, 
                          imageUrl: verification.company.signedCertificateURL, 
                          altText: 'Signed Certificate Document' 
                        })}
                        onError={(e) => handleImageError(e, verification.company.signedCertificateURL)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Verification Status */}
          <div className="bg-white border rounded-lg p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">{t("userDetails.verificationStatus")}</h3>
            <div className="flex items-center justify-between">
              <span className={`px-4 py-2 text-sm font-medium rounded-full ${
                verification.status === 'approved'
                  ? 'bg-green-100 text-green-800'
                  : verification.status === 'rejected'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {verification.status === 'approved'
                  ? `✓ ${t("userDetails.verified")}`
                  : verification.status === 'rejected'
                  ? `✗ ${t("userDetails.rejected")}`
                  : `⏳ ${t("userDetails.pendingVerification")}`}
              </span>

              <div className="flex space-x-3">
                {/* Update Status button - appears for both personal and company */}
                <button
                  onClick={() => setVerificationModalOpen(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {t("userDetails.updateStatus")}
                </button>

                {/* Additional buttons only for company verification */}
                {verificationType === 'company' && (
                  <>
                    <button
                      onClick={handleSyncWithMyFatorah}
                      disabled={syncingMyFatorah}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {syncingMyFatorah ? t("userDetails.syncing") : t("userDetails.syncFatoorah")}
                    </button>

                    <button
                      onClick={handleCheckMyFatorahStatus}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      {t("userDetails.checkFatoorah")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === "clips") {
      // Filter clips if specific clip ID is provided in URL
      const displayClips = clipIdParam 
        ? clips.filter(clip => clip._id === clipIdParam)
        : clips;

      return (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {clipIdParam ? t("userDetails.selectedClip") : t("userDetails.clipsTitle")}
            </h2>
            {clipIdParam && displayClips.length > 0 && (
              <span className="text-sm text-gray-600">
                {t("userDetails.viewingSpecificClip")}
              </span>
            )}
            {!clipIdParam && clipsTotal > 0 && (
              <span className="text-sm text-gray-600">
                {t("userDetails.totalLabel")} {clipsTotal} {t("userDetails.clipsWord")}
              </span>
            )}
          </div>

          {loadingClips ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2">{t("userDetails.loadingClips")}</span>
            </div>
          ) : errorClips ? (
            <p className="text-red-500">{errorClips}</p>
          ) : displayClips.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {clipIdParam ? t("userDetails.clipNotFound") : t("userDetails.noClips")}
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-themeSemiGray sm:pl-6 lg:pl-8">
                          {t("userDetails.col.srNo")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.clipImage")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.description")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.uploaded")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.status")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.price")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.quantity")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.expiryDate")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {displayClips.map((c, i) => (
                        <tr key={c._id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6 lg:pl-8">
                            {clipIdParam ? 1 : (clipsCurrentPage - 1) * ITEMS_PER_PAGE + i + 1}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            <button
                              onClick={() => openVideoModal(c.videoUrl)}
                              className="block truncate hover:opacity-80 transition"
                            >
                              <img
                                src={c.thumbnail}
                                alt="clip"
                                className="h-10 w-16 object-cover rounded shadow-sm"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="40"%3E%3Crect fill="%23f3f4f6" width="64" height="40"/%3E%3Ctext fill="%239ca3af" font-size="10" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo image%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            </button>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-900 max-w-xs">
                            <div className="truncate" title={c.description}>
                              {truncateWords(c.description, 5)}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {new Date(c.uploadDate).toLocaleString()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {(() => {
                              const s = c.status;
                              const cls =
                                s === 'active' ? 'bg-green-100 text-green-700' :
                                s === 'blocked' || s === 'deleted' ? 'bg-red-100 text-red-700' :
                                s === 'expired' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-700';
                              const dot =
                                s === 'active' ? 'bg-green-500' :
                                s === 'blocked' || s === 'deleted' ? 'bg-red-500' :
                                s === 'expired' ? 'bg-orange-500' :
                                'bg-gray-400';
                              return (
                                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${cls}`}>
                                  <span className={`inline-block w-2 h-2 ${dot} rounded-full mr-1`} />
                                  {tStatus(s)}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            ${c.price}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {c.quantity}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {new Date(c.expiryDate).toLocaleString()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium">
                            <button
                              onClick={() => {
                                const isBlocked = c.status === 'blocked' || c.status === 'inactive';
                                const newStatus = isBlocked ? 'active' : 'blocked';
                                const action = isBlocked ? t("userDetails.unblock") : t("userDetails.block");
                                if (window.confirm(t("userDetails.confirmBlockClip").replace("{action}", action))) {
                                  dispatch(updateUserClipStatus({ clipId: c._id, status: newStatus }));
                                }
                              }}
                              className={`px-3 py-1 text-xs rounded transition-colors ${
                                c.status === 'blocked' || c.status === 'inactive'
                                  ? 'bg-green-100 text-green-800 hover:bg-green-600 hover:text-white'
                                  : 'bg-red-100 text-red-800 hover:bg-red-600 hover:text-white'
                              }`}
                            >
                              {c.status === 'blocked' || c.status === 'inactive' ? t("userDetails.unblock") : t("userDetails.block")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Clips Pagination - only show if not filtering specific clip */}
              {!clipIdParam && clipsTotalPages > 1 && (
                <Pagination
                  currentPage={clipsCurrentPage}
                  totalPages={clipsTotalPages}
                  onPageChange={handleClipsPageChange}
                  total={clipsTotal}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              )}

              {/* See All button - only show if viewing a specific clip */}
              {clipIdParam && (
                <div className="mt-4">
                  <button
                    onClick={handleSeeAllClips}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-500 rounded-md hover:bg-purple-600 transition"
                  >
                    {t("userDetails.seeAllClips")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )
    }

    if (activeTab === "auction") {
      // Filter auctions if specific auction ID is provided in URL
      const displayAuctions = auctionIdParam 
        ? auctions.filter(auction => auction._id === auctionIdParam)
        : auctions;

      return (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {auctionIdParam ? t("userDetails.selectedAuction") : t("userDetails.auctionsTitle")}
            </h2>
            {auctionIdParam && displayAuctions.length > 0 && (
              <span className="text-sm text-gray-600">
                {t("userDetails.viewingSpecificAuction")}
              </span>
            )}
            {!auctionIdParam && auctionsTotal > 0 && (
              <span className="text-sm text-gray-600">
                {t("userDetails.totalLabel")} {auctionsTotal} {t("userDetails.auctionsWord")}
              </span>
            )}
          </div>

          {loadingAuctions ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2">{t("userDetails.loadingAuctions")}</span>
            </div>
          ) : errorAuctions ? (
            <p className="text-red-500">{errorAuctions}</p>
          ) : displayAuctions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {auctionIdParam ? t("userDetails.auctionNotFound") : t("userDetails.noAuctions")}
              </p>
            </div>
          ) : (
            <>
              {/* Auctions Table - matching main auction table structure */}
              <div className="px-4 sm:px-4 lg:px-4 bg-white rounded-lg overflow-hidden">
                <div className="flex flex-col">
                  <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle">
                      <div className="overflow-hidden shadow-sm ring-1 ring-black ring-opacity-5">
                        <table className="min-w-full divide-y divide-gray-100">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-6">
                                #
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                {t("userDetails.col.thumbnail")}
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                {t("userDetails.col.title")}
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                {t("userDetails.col.startTime")}
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                {t("userDetails.col.endTime")}
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                {t("userDetails.col.lots")}
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                {t("userDetails.col.bidderType")}
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                {t("userDetails.col.entryAmount")}
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                {t("userDetails.col.status")}
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                {t("userDetails.col.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {displayAuctions.map((a, i) => {
                              const isBlocked = a.status === 'blocked';
                              const label = isBlocked ? t("userDetails.unblock") : t("userDetails.block");
                              const newStatus = isBlocked ? 'live' : 'blocked';

                              return (
                                <tr key={a._id} className="hover:bg-gray-50">
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                    {auctionIdParam ? 1 : (auctionsCurrentPage - 1) * ITEMS_PER_PAGE + i + 1}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    <img
                                      src={a.thumbnail}
                                      alt="thumb"
                                      className="h-12 w-20 object-cover rounded border"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="48"%3E%3Crect fill="%23f3f4f6" width="80" height="48"/%3E%3Ctext fill="%239ca3af" font-size="10" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo image%3C/text%3E%3C/svg%3E';
                                      }}
                                    />
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    <div className="max-w-32 truncate" title={a.title}>
                                      {a.title}
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    <div>{new Date(a.startTime).toLocaleDateString()}</div>
                                    <div className="text-xs text-gray-400">{new Date(a.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    <div>{new Date(a.endTime).toLocaleDateString()}</div>
                                    <div className="text-xs text-gray-400">{new Date(a.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {a.lotIds?.length || 0}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {a.bidderType}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {a.entryAmount || t("userDetails.free")}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                      a.status === 'live' ? 'bg-green-100 text-green-800' :
                                      a.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                      a.status === 'blocked' ? 'bg-red-100 text-red-800' :
                                      a.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                      a.status === 'scheduled' ? 'bg-purple-100 text-purple-800' :
                                      a.status === 'cancelled' ? 'bg-orange-100 text-orange-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {tStatus(a.status)}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm font-medium">
                                    {a.status !== 'completed' && (
                                      <button
                                        onClick={() => {
                                          const action = isBlocked ? t("userDetails.unblock") : t("userDetails.block");
                                          if (window.confirm(t("userDetails.confirmBlockAuction").replace("{action}", action))) {
                                            dispatch(updateUserAuctionStatus({ auctionId: a._id, status: newStatus }));
                                          }
                                        }}
                                        className={`px-3 py-1 text-xs rounded transition-colors ${
                                          isBlocked
                                            ? 'bg-green-100 text-green-800 hover:bg-green-600 hover:text-white'
                                            : 'bg-red-100 text-red-800 hover:bg-red-600 hover:text-white'
                                        }`}
                                      >
                                        {label}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Auctions Pagination - only show if not filtering specific auction */}
                {!auctionIdParam && auctionsTotalPages > 1 && (
                  <Pagination
                    currentPage={auctionsCurrentPage}
                    totalPages={auctionsTotalPages}
                    onPageChange={handleAuctionsPageChange}
                    total={auctionsTotal}
                    itemsPerPage={ITEMS_PER_PAGE}
                  />
                )}

                {/* See All button - only show if viewing a specific auction */}
                {auctionIdParam && (
                  <div className="mt-4">
                    <button
                      onClick={handleSeeAllAuctions}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-500 rounded-md hover:bg-purple-600 transition"
                    >
                      {t("userDetails.seeAllAuctions")}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      );
    }

    if (activeTab === "orders") {
      const displayOrders = orderIdParam
        ? userOrders.filter((o) => o._id === orderIdParam || o.customOrderId === orderIdParam)
        : userOrders;

      const fmtCurrency = (amt, ccy = "KWD") =>
        amt == null ? "—" : `${Number(amt).toFixed(2)} ${ccy}`;

      return (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {orderIdParam ? t("userDetails.selectedOrder") : t("userDetails.ordersTitle")}
            </h2>
            {orderIdParam && displayOrders.length > 0 && (
              <span className="text-sm text-gray-600">
                {t("userDetails.viewingSpecificOrder")}
              </span>
            )}
            {!orderIdParam && ordersTotal > 0 && (
              <span className="text-sm text-gray-600">
                {t("userDetails.totalLabel")} {ordersTotal} {t("userDetails.ordersWord")}
              </span>
            )}
          </div>

          {loadingOrders ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2">{t("userDetails.loadingOrders")}</span>
            </div>
          ) : errorOrders ? (
            <p className="text-red-500">{errorOrders}</p>
          ) : displayOrders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {orderIdParam ? t("userDetails.orderNotFound") : t("userDetails.noOrders")}
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-themeSemiGray sm:pl-6 lg:pl-8">
                          {t("userDetails.col.srNo")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.orderNo")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.invoiceId")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.total")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.orderStatus")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.deliveryStatus")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.createdAt")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("userDetails.col.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {displayOrders.map((o, i) => (
                        <tr key={o._id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6 lg:pl-8">
                            {orderIdParam ? 1 : (ordersCurrentPage - 1) * ITEMS_PER_PAGE + i + 1}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-dashboardCoral">
                            #{o.customOrderId || o._id?.slice(-6) || "—"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {o.invoiceId || "—"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {fmtCurrency(o.invoiceValue, o.displayCurrencyIso)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4">
                            <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {tStatus(o.orderStatus)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4">
                            <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                              {tStatus(o.deliveryStatus)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {o.createdAt ? new Date(o.createdAt).toLocaleString() : "—"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4">
                            <button
                              onClick={() => setOrderDetailsToView(o)}
                              className="px-3 py-1 text-xs rounded bg-blue-100 text-blue-800 hover:bg-blue-600 hover:text-white transition-colors"
                            >
                              {t("orderModal.detailsTitle")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {!orderIdParam && ordersTotalPages > 1 && (
                <Pagination
                  currentPage={ordersCurrentPage}
                  totalPages={ordersTotalPages}
                  onPageChange={handleOrdersPageChange}
                  total={ordersTotal}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              )}

              {orderIdParam && (
                <div className="mt-4">
                  <button
                    onClick={handleSeeAllOrders}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-500 rounded-md hover:bg-purple-600 transition"
                  >
                    {t("userDetails.seeAllOrders")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    if (activeTab === "channels") {
      const displayChannels = channelIdParam
        ? userChannels.filter((c) => c._id === channelIdParam)
        : userChannels;

      const fmtDate = (iso) => {
        if (!iso) return "—";
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
      };

      return (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {channelIdParam ? t("userDetails.selectedChannel") : t("userDetails.channelsTitle")}
            </h2>
            {channelIdParam && displayChannels.length > 0 && (
              <span className="text-sm text-gray-600">
                {t("userDetails.viewingSpecificChannel")}
              </span>
            )}
            {!channelIdParam && channelsTotal > 0 && (
              <span className="text-sm text-gray-600">
                {t("userDetails.totalLabel")} {channelsTotal} {t("userDetails.channelsWord")}
              </span>
            )}
          </div>

          {loadingChannels ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2">{t("userDetails.loadingChannels")}</span>
            </div>
          ) : errorChannels ? (
            <p className="text-red-500">{errorChannels}</p>
          ) : displayChannels.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {channelIdParam ? t("userDetails.channelNotFound") : t("userDetails.noChannels")}
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-themeSemiGray sm:pl-6 lg:pl-8">
                          {t("userDetails.col.srNo")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("channels.col.title")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("channels.col.category")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("channels.col.startTime")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("channels.col.duration")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("channels.col.viewsCount")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("channels.col.totalOrders")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-themeSemiGray">
                          {t("channels.col.status")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {displayChannels.map((c, i) => (
                        <tr key={c._id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6 lg:pl-8">
                            {channelIdParam ? 1 : (channelsCurrentPage - 1) * ITEMS_PER_PAGE + i + 1}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-dashboardDarkText">
                            <div className="flex items-center gap-2">
                              {c.thumbnail && (
                                <img src={c.thumbnail} alt="" className="h-8 w-8 rounded object-cover" />
                              )}
                              <span className="truncate max-w-[14rem]" title={c.title}>
                                {c.title || t("channels.untitled")}
                              </span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {c.categoryId?.categoryName || "—"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {fmtDate(c.startTime)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {c.duration != null ? `${c.duration} ${t("channels.minutes")}` : "—"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {c.viewsCount || 0}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {c.ordersCount || 0}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4">
                            <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {tStatus(c.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {!channelIdParam && channelsTotalPages > 1 && (
                <Pagination
                  currentPage={channelsCurrentPage}
                  totalPages={channelsTotalPages}
                  onPageChange={handleChannelsPageChange}
                  total={channelsTotal}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              )}

              {channelIdParam && (
                <div className="mt-4">
                  <button
                    onClick={handleSeeAllChannels}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-500 rounded-md hover:bg-purple-600 transition"
                  >
                    {t("userDetails.seeAllChannels")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return null;
  };

  if (loading) return <p className="p-4">{t("userDetails.loading")}</p>;
  if (error)   return <p className="p-4 text-red-500">{error}</p>;
  if (!userDetails) return null;

  const tabs = renderTabs();

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-center mb-8">
        <Avatar
          src={userDetails.profilePicture}
          name={userDetails.name}
          size="3xl"
        />
        <div className="ml-6">
          <h1 className="text-3xl font-bold">{userDetails.name}</h1>
          <p className="text-gray-500">
            {tStatus(userDetails.accountStatus)}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <nav className="border-b mb-6">
        <ul className="flex space-x-6">
          {tabs.map(tab => (
            <li key={tab.key}>
              <button
                onClick={() => handleTabChange(tab.key)}
                className={`pb-2 text-sm font-medium ${
                  activeTab === tab.key
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Content */}
      <div className={['auction', 'clips', 'orders', 'channels'].includes(activeTab) ? 'overflow-x-auto' : ''}>
        {renderActiveTabContent()}
      </div>

      {videoModalData.open && (
        <VideoModal
          videoUrl={videoModalData.videoUrl}
          onClose={() => setVideoModalData({ open: false, videoUrl: "" })}
        />
      )}

      {/* Verification Status Modal */}
      <VerificationStatusModal
        isOpen={verificationModalOpen}
        onClose={() => setVerificationModalOpen(false)}
        userId={userDetails?._id}
        currentStatus={userDetails?.identityVerification?.status || 'pending'}
        currentRemarks={userDetails?.identityVerification?.remarks || ''}
      />

      {/* MyFatorah Status Modal - New addition */}
      <MyFatorahStatusModal
        isOpen={myFatorahStatusModalOpen}
        onClose={() => setMyFatorahStatusModalOpen(false)}
        statusData={myFatorahStatusData}
      />

      {/* Image Modal - New addition for full-size image viewing */}
      <ImageModal
        isOpen={imageModalData.open}
        onClose={() => setImageModalData({ open: false, imageUrl: '', altText: '' })}
        imageUrl={imageModalData.imageUrl}
        altText={imageModalData.altText}
      />

      {/* Order Details Modal (Orders tab) */}
      {orderDetailsToView && (
        <OrderDetailsModal
          order={orderDetailsToView}
          isOpen={!!orderDetailsToView}
          onClose={() => setOrderDetailsToView(null)}
        />
      )}
    </div>
  );
};

export default UserDetailsPage;
